const FENCED_CODE_BLOCK_PATTERN = /(```[\s\S]*?```)/g;
const INLINE_CODE_PATTERN = /(`[^`\n]+`)/g;

/** GFM alignment chars; built at runtime so Tailwind content scan does not treat the char class as a utility. */
const GFM_ALIGNMENT_CHAR_CLASS = `[${['-', ':'].join('')}\\s]`;

const COLLAPSED_TABLE_DELIMITER_CELL_PATTERN = new RegExp(`${GFM_ALIGNMENT_CHAR_CLASS}*-${GFM_ALIGNMENT_CHAR_CLASS}*`);

const COLLAPSED_TABLE_DELIMITER_PATTERN = new RegExp(
  `\\| +\\|(?:${COLLAPSED_TABLE_DELIMITER_CELL_PATTERN.source}\\|)+`,
);

const COLLAPSED_TABLE_LINE_PATTERN = new RegExp(
  `\\| +\\|(?:${COLLAPSED_TABLE_DELIMITER_CELL_PATTERN.source}\\|)+\\s*\\|[^|\\n]+\\|`,
);

const countCollapsedTableDelimiterColumns = (delimiterMatch: string): number => {
  const inner = delimiterMatch.replace(/^\|\s+\|/, '');

  return inner.split('|').filter((cell) => COLLAPSED_TABLE_DELIMITER_CELL_PATTERN.test(cell)).length;
};

const findHeaderStartForDelimiterColumnCount = (beforeDelimiter: string, columnCount: number): number => {
  const trimmed = beforeDelimiter.trimEnd();

  if (!trimmed.endsWith('|') || columnCount < 1) {
    return -1;
  }

  const pureHeaderPattern = new RegExp(`^\\|(?:[^|\\n]+\\|){${columnCount}}\\s*$`);

  if (pureHeaderPattern.test(trimmed)) {
    return trimmed.indexOf('|');
  }

  const pipeIndices: number[] = [];

  for (let index = 0; index < trimmed.length; index += 1) {
    if (trimmed[index] === '|') {
      pipeIndices.push(index);
    }
  }

  if (pipeIndices.length < columnCount + 1) {
    return -1;
  }

  return pipeIndices[pipeIndices.length - columnCount - 1] ?? -1;
};

/** Row joins on a collapsed line use spaces, not newlines — do not match across table blocks. */
const COLLAPSED_TABLE_ROW_JOIN_PATTERN = /\| +\|/g;

const findCollapsedTableStart = (line: string): number => {
  const delimiterPattern = new RegExp(COLLAPSED_TABLE_DELIMITER_PATTERN.source, 'g');
  let delimiterMatch = delimiterPattern.exec(line);

  while (delimiterMatch !== null) {
    const columnCount = countCollapsedTableDelimiterColumns(delimiterMatch[0]);
    const headerThroughClosingPipe = line.slice(0, delimiterMatch.index + 1);
    const headerStart = findHeaderStartForDelimiterColumnCount(headerThroughClosingPipe, columnCount);

    if (headerStart !== -1 && COLLAPSED_TABLE_LINE_PATTERN.test(line.slice(headerStart))) {
      return headerStart;
    }

    delimiterMatch = delimiterPattern.exec(line);
  }

  return -1;
};

/** Table row followed by multi-word prose on the same line, e.g. `| a | b | Summary text here`. */
const TABLE_ROW_WITH_TRAILING_PROSE_PATTERN = /^(\|(?:[^|\n]+\|)+\s*)(\S+\s+[A-Za-z][^|\n]+)$/;

const splitTrailingProseFromTableLines = (segment: string): string =>
  segment
    .split('\n')
    .map((line) => {
      if (line.includes('```')) {
        return line;
      }

      const match = line.match(TABLE_ROW_WITH_TRAILING_PROSE_PATTERN);

      if (!match) {
        return line;
      }

      const [, tableRow, prose] = match;

      return `${tableRow.trimEnd()}\n${prose.trim()}`;
    })
    .join('\n');

const normalizeCollapsedTableText = (text: string): string => {
  if (!COLLAPSED_TABLE_LINE_PATTERN.test(text)) {
    return text;
  }

  const withRowBreaks = text.replace(COLLAPSED_TABLE_ROW_JOIN_PATTERN, '|\n|');

  return splitTrailingProseFromTableLines(withRowBreaks);
};

const normalizeCollapsedTableLinePart = (line: string): string => {
  const tableStart = findCollapsedTableStart(line);

  if (tableStart === -1) {
    return line;
  }

  const leadingRaw = line.slice(0, tableStart);
  const leadingText = leadingRaw.trimEnd();
  const normalizedTable = normalizeCollapsedTableText(line.slice(tableStart));

  // Indented markdown code examples use leading whitespace before pipe rows.
  if (!leadingText && leadingRaw.length > 0) {
    return line;
  }

  if (!leadingText) {
    return normalizedTable;
  }

  return `${leadingText}\n${normalizedTable}`;
};

const INLINE_CODE_PLACEHOLDER_PREFIX = '__WORKSPACE_AGENT_INLINE_CODE_';

const normalizeCollapsedTableLine = (line: string): string => {
  const placeholders: string[] = [];
  const stripped = line.replace(INLINE_CODE_PATTERN, (match) => {
    placeholders.push(match);

    return `${INLINE_CODE_PLACEHOLDER_PREFIX}${placeholders.length - 1}__`;
  });

  const normalized = normalizeCollapsedTableLinePart(stripped);
  const placeholderPattern = new RegExp(`${INLINE_CODE_PLACEHOLDER_PREFIX}(\\d+)__`, 'g');

  return normalized.replace(placeholderPattern, (_match, index: string) => placeholders[Number(index)] ?? '');
};

const normalizeCollapsedTableSegment = (segment: string): string => {
  if (!COLLAPSED_TABLE_DELIMITER_PATTERN.test(segment)) {
    return segment;
  }

  return segment.split('\n').map(normalizeCollapsedTableLine).join('\n');
};

/**
 * Best-effort repair for collapsed GFM tables the backend sometimes emits on one line.
 * When uncertain, returns the input unchanged so prose stays readable.
 *
 * Known limitations (best-effort):
 * - Spaced empty cells (`| one | | two |`) are indistinguishable from collapsed row joins.
 * - Interstitial prose between two collapsed tables on one line is not split out.
 * - Row joins apply only to lines containing a collapsed delimiter, not the whole message segment.
 */
export const normalizeSingleLineMarkdownTables = (content: string): string => {
  if (!content.includes('|')) {
    return content;
  }

  const segments = content.split(FENCED_CODE_BLOCK_PATTERN);

  return segments
    .map((segment, index) => {
      // Odd indices are captured fenced code blocks — never rewrite inside them.
      if (index % 2 === 1) {
        return segment;
      }

      return normalizeCollapsedTableSegment(segment);
    })
    .join('');
};
